import React, {CSSProperties, PureComponent, ReactNode} from 'react';
import {PopupDialog} from 'app/utils/modals/PopupDialog';
import {translatedName} from 'app/utils/translations_helper';
import PopupFooter from './popup/popup_footer';
import InventoryPopupContent from './InventoryPopupContent';
import OpenedLootboxPopupContent from './OpenedLootboxPopupContent';
import Conversion from './artifact_popup/Conversion';
import {Provider} from 'mobx-react';
import {modalsStore, promoActionStore, userBonusesStore} from 'app/store/global';
import {onCloseItemIntentory} from 'app/utils/CloseInventoryPopup';
import NotificationStore from 'app/store/notifications';
import {isLootBox} from 'app/utils/isLootBox';
import InventoryItemInterface from 'app/interfaces/InventoryItemInterface';
import InventoryCommonPropertiesInterface from 'app/interfaces/InventoryCommonPropertiesInterface';
import LootInterface from 'app/interfaces/LootInterface';
import SteamSkinInterface from 'app/interfaces/SteamSkinInterface';

interface ArtifactPopupOptions {
    extra_data?: LootInterface[];
    steam_skin?: SteamSkinInterface;
    steam_trade?: SteamSkinInterface;
    win_artifact?: InventoryItemInterface;
    onLootboxPrizePopupClose?: () => void;
}

interface Props {
    artifact: InventoryItemInterface;
    options?: ArtifactPopupOptions;
    popupId: string;
    useNow?: boolean;
}

interface State {
    animationOpenLootboxInvEnd: boolean;
    contentMinHeight: number | null;
    disassembling: boolean;
    disassemblingError: boolean;
    openingLootbox: boolean;
    responseJSON: ArtifactPopupOptions;
}

export default class ArtifactPopup extends PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        const options = props.options || {};

        this.state = {
            animationOpenLootboxInvEnd: Object.keys(options).length > 0,
            contentMinHeight: null,
            disassembling: false,
            disassemblingError: false,
            openingLootbox: false,
            responseJSON: options
        };
    }

    isLootbox = (): boolean => isLootBox(this.props.artifact.item_type);

    isArtifact = (): boolean => !this.isLootbox();

    close = (): void => {
        modalsStore.hideModal(this.props.popupId);
    };

    onCloseInventoryPopup = (): void => {
        const {animationOpenLootboxInvEnd, openingLootbox} = this.state;

        if (!animationOpenLootboxInvEnd && openingLootbox) {
            NotificationStore.add({
                type: 'leave_lootbox_page'
            });
        }
    };

    onClose = (): void => {
        const {animationOpenLootboxInvEnd} = this.state;
        const {options} = this.props;

        onCloseItemIntentory();
        options?.onLootboxPrizePopupClose?.();

        if (this.isLootbox() && animationOpenLootboxInvEnd && !options?.onLootboxPrizePopupClose) {
            userBonusesStore.showNotViewedBonusPopup(true);
        }
    };

    publicView = (): boolean => this.props.artifact.public_view;

    handleAnimationEnded = (): void => {
        this.setState({animationOpenLootboxInvEnd: true});
    };

    propsUpdateState = (state: {
        contentMinHeight?: number | null;
        openingLootbox?: boolean;
        responseJSON?: ArtifactPopupOptions;
    }): void => {
        this.setState(prevState => ({
            ...prevState,
            ...state
        }));
    };

    popupContentClassName = (lootboxOpening: boolean, lootboxPrize: boolean): string => {
        const classNames = ['popup__content'];

        if (lootboxOpening || lootboxPrize) {
            classNames.push('popup__content--lootbox-opening');
        }

        if (lootboxPrize) {
            classNames.push('popup__content--lootbox-prize');
        }

        return classNames.join(' ');
    };

    popupContentStyle = (lootboxOpening: boolean, contentMinHeight: number | null): CSSProperties => {
        if (!contentMinHeight) {
            return {};
        }

        if (lootboxOpening) {
            return {height: contentMinHeight, overflow: 'hidden'};
        }

        return {minHeight: contentMinHeight};
    };

    render(): ReactNode {
        const {artifact, useNow = false, popupId} = this.props;
        const {
            openingLootbox,
            animationOpenLootboxInvEnd,
            contentMinHeight,
            responseJSON = {}
        } = this.state;
        const lootboxOpening = this.isLootbox() && openingLootbox && !animationOpenLootboxInvEnd;
        const lootboxPrize = this.isLootbox() && animationOpenLootboxInvEnd;
        const {extra_data = [], steam_trade, steam_skin, win_artifact} = responseJSON;

        const commonProperties: InventoryCommonPropertiesInterface = {
            animationOpenLootboxInvEnd,
            extra_data,
            handleAnimationEnded: this.handleAnimationEnded,
            isArtifact: this.isArtifact(),
            isLootbox: this.isLootbox(),
            onCloseInventoryPopup: this.onCloseInventoryPopup,
            openingLootbox,
            steam_skin,
            steam_trade,
            useNow,
            win_artifact
        };

        return <PopupDialog id={popupId} onClose={this.onClose}>
            <div
                className={this.popupContentClassName(lootboxOpening, lootboxPrize)}
                style={this.popupContentStyle(lootboxOpening, contentMinHeight)}
            >
                <div className="popup__header-slide">
                    <div className="popup__header-slide-inner">
                        <h2 className="popup__header">
                            {!animationOpenLootboxInvEnd && translatedName(artifact)}
                        </h2>
                    </div>
                </div>
                {animationOpenLootboxInvEnd
                    ? <OpenedLootboxPopupContent
                        artifact={artifact}
                        commonProperties={commonProperties}
                    />
                    : <InventoryPopupContent
                        commonProperties={commonProperties}
                        artifact={artifact}
                        close={this.close}
                        propsUpdateState={this.propsUpdateState}
                    />}
            </div>
            <PopupFooter
                {...artifact}
                publicView={this.publicView}
                close={this.close}
            />
            {!this.publicView() && <Provider promoActionStore={promoActionStore}>
                <Conversion
                    artifact={{
                        id: artifact.id,
                        item_type: artifact.item_type,
                        langs: artifact.langs,
                        name: artifact.name
                    }}
                    close={this.close}
                />
            </Provider>}
        </PopupDialog>;
    }
}
